import React, { useEffect, useState } from 'react';
import Header from '../header';
import Questionnaire from '../questionnaire';
import {
  decodedTokenAtom,
  verificationTokenAtom,
  careProviderConfiguration,
} from '../../store';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useGetQuery } from '../../api/hooks/useGetQuery';
import { QuestionnaireData } from '../../models/questionnaire.interface';
import { FeedbackSurveyDataType } from '../../models/feedback_survey.interface';
import { Typography } from 'antd';
import styles from './survey_page.module.scss';
import LoadingCard from '../loading_card';
import ErrorAlert from '../error_alert';
import { COLORS } from 'themes/colors';

const { Text } = Typography;

const SurveyPage: React.FC = () => {
  const [token] = useAtom(verificationTokenAtom);
  const [decodedToken] = useAtom(decodedTokenAtom);
  const [careProviderConfig] = useAtom(careProviderConfiguration);
  const patientId = decodedToken?.patientId;
  const [feedbackScriptId, setFeedbackScriptId] = useState(0);

  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };
  const routeParams = useParams();
  const scriptId = Number(routeParams.scriptId);

  const [searchParams] = useSearchParams();
  const encounterId = searchParams.get('encounterId');
  const unitId = searchParams.get('unitId');
  const staffMemberId = searchParams.get('staffMemberId');
  const [initialInteractionIdParam] = useState(() => searchParams.get('interactionId'));

  const hasPatientContext = !!(scriptId && patientId && encounterId && unitId);
  const hasStaffContext = !!(scriptId && staffMemberId);

  // Anonymous sessions must not resume a prior (e.g. bookmarked) interaction.
  const isAnonymousResumeAttempt
    = hasStaffContext && !!decodedToken?.anonymous && initialInteractionIdParam != null;

  const { data, exec, isError, isSuccess, isPending }
    = useGetQuery<QuestionnaireData>(`scripts/${scriptId}`, config);

  const { data: feedbackSurveyData, exec: getFeedbackSurvey }
    = useGetQuery<FeedbackSurveyDataType>(
      `feedback_scripts/${feedbackScriptId}`,
      config,
    );

  // When the component mounts, get script data.
  // Both decodedToken and URL params are available synchronously on first render.
  useEffect(() => {
    if ((hasPatientContext || hasStaffContext) && !isAnonymousResumeAttempt) {
      exec();
    }
  }, []);

  // Once we have script data and a feedbackScriptId, set the feedbackScriptId in state.
  useEffect(() => {
    if (isSuccess && data?.feedbackScriptId) {
      setFeedbackScriptId(data?.feedbackScriptId);
    }
  }, [data]);

  // Once feedbackScriptId is available in state, retrieve the feedbackSurveyData.
  useEffect(() => {
    if (isSuccess && data) {
      getFeedbackSurvey();
    }
  }, [feedbackScriptId]);

  if (isAnonymousResumeAttempt) {
    return <Navigate to="../../landing-page" relative="path" />;
  }

  return (
    <LoadingCard isLoading={isPending}>
      <Header title={careProviderConfig.botName} background={COLORS.whiteL0} />
      {isSuccess && data && (
        <Questionnaire
          questionnaireData={data}
          feedbackSurveyData={feedbackSurveyData}
          token={token}
        />
      )}
      {isSuccess && (!data || !Object.keys(data).length) && (
        <Text className={styles.errorStyle} type="danger">
          No questionnaire available
        </Text>
      )}
      {isError && (
        <ErrorAlert message="There was an error loading your information. Please try again later." />
      )}
    </LoadingCard>
  );
};

export default SurveyPage;
